import React, { useEffect, useState } from "react";
import { Col, Container, Row, Card, Spinner } from "react-bootstrap";
import { useSupabaseApp } from "../../SupabaseApp";
import { BiAnalyse } from "react-icons/bi";
import { AiOutlineCloseCircle } from "react-icons/ai";
import PRNewswireLogo from "../../../assets/dashboard/prn_cision_logo.png";
import "./rss-feed.css";

/**
 * FeedCard Component
 * 
 * Displays a single RSS feed item (news article, press release) with title,
 * publication date, summary text, and optional image. Extracts clean text
 * from HTML descriptions and handles missing images with fallback placeholder.
 * 
 * @param {Object} props - Component props
 * @param {Object} props.item - Feed item object with title, description, pubDate, link, and optional img
 * @returns {JSX.Element} Card component displaying formatted feed item
 */
const FeedCard = ({ item }) => {
  /**
   * Extract clean text summary from HTML description
   * Parses HTML content and returns plain text without tags
   * 
   * @param {string} html - HTML string from feed description
   * @returns {string} Plain text summary
   */
  const getSummary = (html) => {
    // Parse HTML string into DOM to extract text content
    const doc = new DOMParser().parseFromString(html, "text/html");
    // Return only the clean text content, stripping all HTML tags
    return doc.body.textContent || "";
  };

  const summaryText = getSummary(item.description);

  return (
    <Card style={{ margin: "0 15px", borderColor: "transparent" }}>
      <Card.Body>
        <Container fluid>
          <Row>
            <Col
              xs={item.img ? 8 : 12}
              sm={item.img ? 8 : 12}
              md={item.img ? 8 : 12}
              lg={item.img ? 8 : 12}
              xl={item.img ? 8 : 12}
              xxl={item.img ? 6 : 12}
            >
              <a href={item.link} target="_blank" rel="noopener noreferrer">
                <p
                  className="dashboardCardData rssFeedHeader"
                  style={{ padding: "0", fontSize: "18px" }}
                >
                  {item.title}
                </p>
              </a>

              <p
                className="dashboardCardLabel"
                style={{ padding: "0", margin: "0" }}
              >
                {new Date(item.pubDate).toLocaleDateString()}
              </p>

              <p
                className="dashboardCardText rssFeedHeader"
                style={{ padding: "0", margin: "0" }}
              >
                {summaryText}
              </p>
            </Col>
            <Col
              xs={4}
              sm={4}
              md={4}
              lg={4}
              xl={4}
              xxl={6}
              style={{ alignContent: "center" }}
            >
              {item.img && (
                <img
                  src={item.img.src}
                  alt={item.img.alt || item.title}
                  // Use placeholder image if the feed image fails to load
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src =
                      "https://placehold.co/96x96/eeeeee/333333?text=No+Img";
                  }}
                  style={{
                    width: "100%",
                    height: "7.5em",
                    objectFit: "contain",
                  }}
                />
              )}
            </Col>
          </Row>
        </Container>
      </Card.Body>
    </Card>
  );
};

// Main App Component
/**
 * FeedDisplay Component
 * 
 * Displays financial and corporate news feeds from RSS sources (via Cision API).
 * Fetches feed data on component mount, manages loading state, and displays
 * feed items using FeedCard component. Includes error handling with loading skeletons.
 * 
 * @param {Object} props - Component props
 * @param {Function} props.setAlert - Function to control alert visibility
 * @param {Function} props.setMessage - Function to set alert message content
 * @param {Function} props.closeAlert - Function to hide alert
 * @returns {JSX.Element} News feed section with article cards or skeleton loaders
 */
const FeedDisplay = (props) => {
  const { supabase } = useSupabaseApp();

  // State to store financial news feed items fetched from Cision API
  const [finItems, setFinItems] = useState([]);

  // State to store corporate press release feed items
  const [cprItems, setCprItems] = useState([]);

  // State to track whether feed data is currently loading
  const [isLoading, setIsLoading] = useState(true);

  // Number of feed items to display per section
  const slice = 7;

  /**
   * Fetch news feed data from Cision API endpoint on component mount
   * Updates finItems and cprItems state with fetched data
   * Displays loading and error states via alert messages
   */
  useEffect(() => {
    async function getNewsFeed() {
      // Show loading alert
      props.setAlert(true);
      props.setMessage(
        <div style={{ display: "flex", paddingTop: "10px" }}>
          <div style={{ width: "90%" }}>
            <p style={{ color: "#0f9d58" }}>
              <BiAnalyse className="alert-icon" /> Loading
            </p>
            <p style={{ marginLeft: "28px", fontSize: "14px" }}>
              Won't be long now. We're always doing our best to reduce waiting
              time.
            </p>
            <p style={{ marginLeft: "28px", fontSize: "14px" }}>
              If your waiting time is too long,{" "}
              <a
                href="https://www.roiquant.com/contact"
                target="_blank"
                rel="noopener noreferrer"
              >
                contact us
              </a>
              .
            </p>
          </div>
          <div style={{ width: "10%", textAlign: "right" }}>
            <Spinner animation="border" variant="secondary" />
          </div>
        </div>
      );

      try {
        const { data, error } = await supabase.functions.invoke(
          "getCisionNews"
        );

        if (error) {
          const ctx = await error.context?.json?.().catch(() => ({}));
          const msg = ctx?.error ?? "unknown";
          throw new Error(msg);
        }

        // // 1. Filtering Logic: Filter items that include 'FIN' in their category array
        // finItems = setFinItems(data.filter(item => item.category.includes('FIN')));

        // // 2. Filtering Logic: Filter items that include 'CPR' in their category array
        // cprItems = setCprItems(data.filter(item => item.category.includes('CPR')));

        // 1. Calculate the filtered arrays. Added defensive check for item.category existence.
        const newFinItems = data.finItems;
        const newCprItems = data.cprItems;

        // 2. ONLY call the state setters.
        setFinItems(newFinItems);
        setCprItems(newCprItems);

        setIsLoading(false);
        props.closeAlert();
      } catch (error) {
        setIsLoading(false);
        props.setAlert(true);
        props.setMessage(
          <div style={{ paddingTop: "10px" }}>
            <p style={{ fontSize: "16px", color: "red" }}>
              <AiOutlineCloseCircle className="alert-icon" /> Error
            </p>
            <p style={{ marginLeft: "28px", fontSize: "14px" }}>
              An unknown error occurred. Please try again. If error persists,{" "}
              <a
                href="https://www.roiquant.com/contact"
                target="_blank"
                rel="noopener noreferrer"
              >
                contact us
              </a>
              .
            </p>
          </div>
        );
      }
    }

    getNewsFeed();
  }, []);

  const SkeletonCard = () => (
    <div className="skeleton-wrapper">
      <div className="skeleton-line long"></div>
      <div className="skeleton-line short"></div>
      <div
        style={{
          width: "100%",
          borderBottom: "1px solid #e5e5e5",
          margin: "15px 0",
        }}
      />
    </div>
  );

  return (
    <>
      <p className="dashboardHeader" style={{ paddingTop: "50px" }}>
        <img src={PRNewswireLogo} style={{ marginTop: "-6px" }} /> is the
        official News Distribution Partner of roiquant
      </p>

      <div
        className="rssFeedContainer"
        style={{ paddingTop: "0px", paddingBottom: "0px" }}
      >
        <Row
          className="d-none d-lg-flex"
          xs={1}
          sm={1}
          md={1}
          lg={2}
          xl={2}
          xxl={2}
        >
          {/* Financial News Column */}
          <Col>
            <Row
              style={{
                justifyContent: "space-between",
                margin: "30px 0 0 0",
              }}
            >
              <p className="dashboardHeader">Financial Services</p>
            </Row>

            <hr />
          </Col>

          {/* Consumer/Retail Column Header */}
          <Col>
            <Row
              style={{
                justifyContent: "space-between",
                margin: "30px 0 0 0",
              }}
            >
              <p className="dashboardHeader">Business Technology</p>
            </Row>

            <hr />
          </Col>
        </Row>
        <div className={`rssFeedSharedScroll ${isLoading ? "loading" : ""}`}>
          <Row xs={1} sm={1} md={1} lg={2} xl={2} xxl={2}>
            {/* FIN feed content */}
            <Col>
              <Row
                className="d-lg-none"
                style={{
                  justifyContent: "space-between",
                  margin: "30px 0 0 0",
                }}
              >
                <p className="dashboardHeader">Financial Services</p>
              </Row>

              <hr className="d-lg-none" />
              <Row className="rssFeedSubContainer">
                {isLoading ? (
                  Array.from({ length: slice }).map((_, i) => (
                    <SkeletonCard key={i} />
                  ))
                ) : finItems && finItems.length > 0 ? (
                  finItems.slice(0, slice).map((item, index) => (
                    <React.Fragment key={index}>
                      <Row style={{ marginBottom: "15px" }}>
                        <FeedCard item={item} />
                      </Row>
                      {index !== slice - 1 && (
                        <div
                          style={{
                            width: "100%",
                            borderBottom: "1px solid #e5e5e5",
                            margin: "15px 0",
                          }}
                        />
                      )}
                    </React.Fragment>
                  ))
                ) : (
                  <p className="dashboardHeader">No data found.</p>
                )}

                {finItems && finItems.length > 0 ? (
                  <div
                    style={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "end",
                    }}
                  >
                    <a
                      className="rssFeedLink rssFeedButton rssFeedCustomButton"
                      href="/prnewswire-fin"
                    >
                      <span>View more</span>
                    </a>
                  </div>
                ) : (
                  ""
                )}
              </Row>
            </Col>

            {/* CPR feed content */}
            <Col>
              <Row
                className="d-lg-none"
                style={{
                  justifyContent: "space-between",
                  margin: "30px 0 0 0",
                }}
              >
                <p className="dashboardHeader">Business Technology</p>
              </Row>

              <hr className="d-lg-none" />
              <Row className="rssFeedSubContainer">
                {isLoading ? (
                  Array.from({ length: slice }).map((_, i) => (
                    <SkeletonCard key={i} />
                  ))
                ) : cprItems && cprItems.length > 0 ? (
                  cprItems.slice(0, slice).map((item, index) => (
                    <React.Fragment key={index}>
                      <Row style={{ marginBottom: "15px" }}>
                        <FeedCard item={item} />
                      </Row>
                      {index !== slice - 1 && (
                        <div
                          style={{
                            width: "100%",
                            borderBottom: "1px solid #e5e5e5",
                            margin: "15px 0",
                          }}
                        />
                      )}
                    </React.Fragment>
                  ))
                ) : (
                  <p className="dashboardHeader">No data found.</p>
                )}

                {cprItems && cprItems.length > 0 ? (
                  <div
                    style={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "end",
                    }}
                  >
                    <a
                      className="rssFeedLink rssFeedButton rssFeedCustomButton"
                      href="/prnewswire-cpr"
                    >
                      <span>View more</span>
                    </a>
                  </div>
                ) : (
                  ""
                )}
              </Row>
            </Col>
          </Row>
        </div>
      </div>
    </>
  );
};

export default FeedDisplay;
